
import { defineComponent } from "vue";
import { listKnowledgeBases } from "@/api/knowledgeBases.api.js";
import AiAgentFollowUpPanel from "@/components/ai/AiAgentFollowUpPanel.vue";
import AiAgentOptionsPanel from "@/components/ai/AiAgentOptionsPanel.vue";
import { clampDelayMinutes } from "@/utils/followUpPolicy.js";

export default defineComponent({
  name: "ai-agent-form",
  components: {
    AiAgentFollowUpPanel,
    AiAgentOptionsPanel
  },
  props: {
    initial: {
      type: Object,
      default: null
    },
    saving: {
      type: Boolean,
      default: false
    },
    submitLabel: {
      type: String,
      default: "Save agent"
    }
  },
  emits: ["submit", "cancel"],
  computed: {
    agentId(): number | null {
      const id = this.initial && this.initial.id;
      return id ? Number(id) : null;
    }
  },
  data() {
    return {
      knowledgeBases: [] as any[],
      form: {
        name: "",
        personality: "",
        goal: "",
        additionalInfo: "",
        knowledgeBaseId: null as number | null,
        summaryEnabled: false,
        summaryInactivityMinutes: 15,
        summaryMinMessages: 3,
        followUpScenarios: {
          contactStoppedReplying: {
            enabled: false,
            name: "Contact Stopped Replying",
            steps: []
          },
          contactIsBusy: {
            enabled: false,
            name: "Contact is Busy",
            steps: []
          },
          contactRequested: {
            enabled: false,
            name: "Contact Requested",
            steps: []
          }
        },
        agentOptions: {
          contactUpdate: {
            askForEmail: false,
            askForPhone: false
          },
          imageReading: {
            enabled: false
          },
          audioReading: {
            enabled: false
          },
          followUp: {
            continueOnEmail: false,
            includeColdLeads: false
          }
        }
      }
    };
  },
  beforeMount() {
    this.applyInitial();
  },
  mounted() {
    this.loadKnowledgeBases();
  },
  watch: {
    initial() {
      this.applyInitial();
    }
  },
  methods: {
    updateFollowUpAgentOptions(value: Record<string, unknown>) {
      const followUp = (value?.followUp || {}) as Record<string, unknown>;
      this.form.agentOptions = {
        ...this.form.agentOptions,
        followUp: {
          continueOnEmail: Boolean(followUp.continueOnEmail),
          includeColdLeads: Boolean(followUp.includeColdLeads)
        }
      };
    },
    async loadKnowledgeBases() {
      try {
        const response = await listKnowledgeBases();
        this.knowledgeBases = response.data?.knowledgeBases || [];
      } catch {
        this.knowledgeBases = [];
      }
    },
    applyInitial() {
      const initial = this.initial || {};
      this.form.name = initial.name || "";
      this.form.personality = initial.personality || "";
      this.form.goal = initial.goal || "";
      this.form.additionalInfo = initial.additionalInfo || "";
      this.form.knowledgeBaseId = initial.knowledgeBaseId ? Number(initial.knowledgeBaseId) : null;
      this.form.summaryEnabled = Boolean(initial.summaryEnabled);
      this.form.summaryInactivityMinutes = Number(initial.summaryInactivityMinutes) || 15;
      this.form.summaryMinMessages = Number(initial.summaryMinMessages) || 3;
      this.form.followUpScenarios = this.normalizeFollowUpScenarios(initial.followUpScenarios);
      this.form.agentOptions = this.normalizeAgentOptions(initial.agentOptions);
    },
    normalizeAgentOptions(input: any) {
      const contactUpdate = input && input.contactUpdate ? input.contactUpdate : {};
      const imageReading = input && input.imageReading ? input.imageReading : {};
      const audioReading = input && input.audioReading ? input.audioReading : {};
      const followUp = input && input.followUp ? input.followUp : {};
      return {
        contactUpdate: {
          askForEmail: Boolean(contactUpdate.askForEmail),
          askForPhone: Boolean(contactUpdate.askForPhone)
        },
        imageReading: {
          enabled: Boolean(imageReading.enabled)
        },
        audioReading: {
          enabled: Boolean(audioReading.enabled)
        },
        followUp: {
          continueOnEmail: Boolean(followUp.continueOnEmail),
          includeColdLeads: Boolean(followUp.includeColdLeads)
        }
      };
    },
    normalizeMediaIds(mediaIds: any) {
      if (!Array.isArray(mediaIds)) {
        return [];
      }
      const unique = Array.from(
        new Set(
          mediaIds
            .map((id: any) => Number(id))
            .filter((id: number) => Number.isInteger(id) && id > 0)
        )
      );
      return unique.slice(0, 4);
    },
    normalizeFollowUpScenarios(input: any) {
      const defaults = {
        contactStoppedReplying: {
          enabled: false,
          name: "Contact Stopped Replying",
          steps: [] as any[]
        },
        contactIsBusy: {
          enabled: false,
          name: "Contact is Busy",
          steps: [] as any[]
        },
        contactRequested: {
          enabled: false,
          name: "Contact Requested",
          steps: [] as any[]
        }
      };
      const source = input && typeof input === "object" ? input : {};

      return {
        contactStoppedReplying: this.normalizeScenario(
          source.contactStoppedReplying,
          defaults.contactStoppedReplying
        ),
        contactIsBusy: this.normalizeScenario(source.contactIsBusy, defaults.contactIsBusy),
        contactRequested: this.normalizeScenario(source.contactRequested, defaults.contactRequested)
      };
    },
    normalizeScenario(scenario: any, fallback: any) {
      const source = scenario && typeof scenario === "object" ? scenario : {};
      return {
        enabled: Boolean(source.enabled),
        name: source.name || fallback.name,
        steps: Array.isArray(source.steps)
          ? source.steps
            .filter((step: any) => step && typeof step === "object")
            .map((step: any) => ({
              delayMinutes: clampDelayMinutes(step.delayMinutes),
              useLlm: step.useLlm !== false,
              message: step.message || "",
              llmHint: step.llmHint || "",
              mediaIds: step.useLlm === false
                ? this.normalizeMediaIds(step.mediaIds)
                : []
            }))
          : []
      };
    },
    submit() {
      this.$emit("submit", {
        name: this.form.name.trim(),
        personality: this.form.personality.trim(),
        goal: this.form.goal.trim(),
        additionalInfo: this.form.additionalInfo.trim(),
        knowledgeBaseId: this.form.knowledgeBaseId || null,
        summaryEnabled: this.form.summaryEnabled,
        summaryMinMessages: this.form.summaryMinMessages,
        followUpScenarios: this.normalizeFollowUpScenarios(this.form.followUpScenarios),
        agentOptions: this.form.agentOptions
      });
    }
  }
});
